import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Cpu, AlertTriangle, Building, Plus, Check, Timer, Send, Trash2, Pencil, Archive, HardDrive, Zap, ShieldCheck, PackagePlus } from 'lucide-react';
import { ticketingApi, type SystemType } from '@/lib/ticketing-api';
import { monitoringSchedulesApi, notificationRecipientsApi, retentionPoliciesApi, dataRetentionApi, assetAutomationApi, firmwareCatalogApi, inventoryApi, type MonitoringSchedule, type RetentionPolicy, type FirmwareCatalogEntry, type RetentionField, type RetentionEnabledField, type DataLossImpact, type NotificationRecipientChannel } from '@/lib/inventory-api';
import { PageHeader, DataTable, type Column } from '@/components/ui/DataTable';
import { Skeleton } from '@/components/ui/primitives';
import { TwoFactorCard } from '@/components/TwoFactorCard';
import { QuickFormModal } from '@/components/QuickFormModal';
import { useToast } from '@/components/ui/Toast';

// Reusable on/off switch (glass). Replaces checkboxes across Settings.
function Toggle({ on, onChange, title }: { on: boolean; onChange: (v: boolean) => void; title?: string }) {
  const { t } = useTranslation('pages');
  return (
    <button
      type="button" role="switch" aria-checked={on} title={title ?? (on ? t('settings.toggle.enabled') : t('settings.toggle.disabled'))}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${on ? 'bg-primary' : 'bg-white/10 ring-1 ring-white/15'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${on ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );
}
const fmtInterval = (ms: number) => (ms % 3600000 === 0 ? `${ms / 3600000}h` : ms % 60000 === 0 ? `${ms / 60000}m` : `${ms / 1000}s`);

const TABS = [
  { id: 'system-types', labelKey: 'settings.tabs.systemTypes', icon: Cpu },
  { id: 'fault-types', labelKey: 'settings.tabs.faultTypes', icon: AlertTriangle },
  { id: 'schedules', labelKey: 'settings.tabs.schedules', icon: Timer },
  { id: 'retention', labelKey: 'settings.tabs.retention', icon: Archive },
  { id: 'assets', labelKey: 'settings.tabs.assets', icon: PackagePlus },
  { id: 'firmware', labelKey: 'settings.tabs.firmware', icon: HardDrive },
  { id: 'recipients', labelKey: 'settings.tabs.recipients', icon: Send },
  { id: 'organization', labelKey: 'settings.tabs.organization', icon: Building },
  // "My notifications" moved to the user Profile (avatar menu) — it's per-user.
  { id: 'security', labelKey: 'settings.tabs.security', icon: ShieldCheck },
];

export function SettingsPage() {
  const { t } = useTranslation('pages');
  const [tab, setTab] = useState('system-types');
  const [addOpen, setAddOpen] = useState(false);          // add system type
  const [schedOpen, setSchedOpen] = useState(false);      // add schedule
  const [retOpen, setRetOpen] = useState(false);          // add retention policy
  const [fwOpen, setFwOpen] = useState(false);            // add firmware-catalog entry
  const [editSched, setEditSched] = useState<MonitoringSchedule | null>(null);
  const [editRet, setEditRet] = useState<RetentionPolicy | null>(null);
  const [editFw, setEditFw] = useState<FirmwareCatalogEntry | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const flash = (m: string) => toast.success(m);
  const { data: systemTypes, isLoading } = useQuery({ queryKey: ['system-types'], queryFn: ticketingApi.listSystemTypes });
  const { data: schedules } = useQuery({ queryKey: ['monitoring-schedules'], queryFn: monitoringSchedulesApi.list, enabled: tab === 'schedules', retry: false });
  const { data: retPolicies } = useQuery({ queryKey: ['retention-policies'], queryFn: retentionPoliciesApi.list, enabled: tab === 'retention', retry: false });
  // Name lookup for scope IDs (so schedules/retention show a name, not a UUID,
  // and the add-forms can offer site/device dropdowns instead of a pasted UUID).
  const scopePicker = tab === 'schedules' || tab === 'retention';
  const { data: scopeSites } = useQuery({ queryKey: ['sites'], queryFn: ticketingApi.listSites, enabled: scopePicker, retry: false });
  const { data: scopeDevices } = useQuery({ queryKey: ['inventory'], queryFn: () => inventoryApi.list(), enabled: scopePicker, retry: false });
  const scopeName = (id?: string | null) => {
    if (!id) return '';
    return scopeSites?.find((s) => s.id === id)?.name
      ?? scopeDevices?.find((d) => d.id === id)?.name
      ?? `${id.slice(0, 8)}…`;
  };
  // Dropdown options for the schedule/retention "target" field — sites then devices.
  const scopeOptions = [
    { value: '', label: t('settings.scope.wholeTenantDash') },
    ...(scopeSites ?? []).map((s) => ({ value: s.id, label: t('settings.scope.siteOption', { name: s.name }) })),
    ...(scopeDevices ?? []).map((d) => ({ value: d.id, label: t('settings.scope.deviceOption', { name: d.name }) })),
  ];
  const { data: fwCatalog } = useQuery({ queryKey: ['firmware-catalog'], queryFn: firmwareCatalogApi.list, enabled: tab === 'firmware', retry: false });
  const { data: recipients } = useQuery({ queryKey: ['notification-recipients'], queryFn: notificationRecipientsApi.list, enabled: tab === 'recipients', retry: false });

  const stColumns: Column<SystemType>[] = [
    { key: 'name', header: t('settings.columns.systemType'), className: 'font-medium' },
    { key: 'code', header: t('settings.columns.code'), className: 'tk-num', render: (s) => <span className="text-muted">{s.code}</span> },
    { key: 'category', header: t('settings.columns.category'), render: (s) => <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-muted ring-1 ring-white/10">{s.category}</span> },
    { key: 'faultTypes', header: t('settings.columns.faultTypes'), render: (s) => (
      <div className="flex flex-wrap gap-1">{s.faultTypes.map((f) => <span key={f} className="rounded bg-white/5 px-1.5 py-0.5 text-[11px] text-muted">{f}</span>)}</div>
    ) },
    { key: 'deviceCount', header: t('settings.columns.devices'), align: 'right', render: (s) => <span className="tk-num">{s.deviceCount}</span> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
        action={
          tab === 'system-types' ? <button className="tk-btn-primary" onClick={() => setAddOpen(true)}><Plus size={16} /> {t('settings.actions.addSystemType')}</button>
          : tab === 'schedules' ? <button className="tk-btn-primary" onClick={() => setSchedOpen(true)}><Plus size={16} /> {t('settings.actions.addSchedule')}</button>
          : tab === 'retention' ? <button className="tk-btn-primary" onClick={() => setRetOpen(true)}><Plus size={16} /> {t('settings.actions.addRetentionPolicy')}</button>
          : tab === 'firmware' ? <button className="tk-btn-primary" onClick={() => setFwOpen(true)}><Plus size={16} /> {t('settings.actions.addFirmwareEntry')}</button>
          : undefined
        }
      />

      {/* ── Edit modals (reuse QuickFormModal prefilled; PATCH the record) ── */}
      {editSched && (
        <QuickFormModal
          title={t('settings.schedules.editTitle')}
          submitLabel={t('settings.actions.save')}
          fields={[
            { key: 'checkType', label: t('settings.fields.checkType', 'Check type'), type: 'select', defaultValue: editSched.checkType, options: [
              { value: 'all', label: 'all' }, { value: 'heartbeat', label: 'heartbeat' }, { value: 'recording', label: 'recording' },
              { value: 'storage', label: 'storage' }, { value: 'clock', label: 'clock' }, { value: 'firmware', label: 'firmware' }, { value: 'video', label: 'video' },
            ] },
            { key: 'intervalMin', label: t('settings.fields.intervalMinutes', 'Interval (minutes)'), type: 'number', required: true, defaultValue: String(Math.round(editSched.intervalMs / 60000)) },
          ]}
          onClose={() => setEditSched(null)}
          onSubmit={async (v) => {
            await monitoringSchedulesApi.update(editSched.id, { checkType: v.checkType, intervalMs: Math.max(5000, (Number(v.intervalMin) || 5) * 60000) });
            queryClient.invalidateQueries({ queryKey: ['monitoring-schedules'] });
            flash(t('settings.toast.scheduleSaved', 'Schedule updated'));
          }}
        />
      )}
      {editRet && (
        <QuickFormModal
          title={t('settings.retention.editTitle')}
          submitLabel={t('settings.actions.save')}
          fields={[
            { key: 'minDays', label: t('settings.fields.minRetentionDays'), type: 'number', required: true, defaultValue: String(editRet.minRetentionDays) },
            { key: 'maxDays', label: t('settings.fields.maxRetentionDays'), type: 'number', required: true, defaultValue: String(editRet.maxRetentionDays) },
            { key: 'enforce', label: t('settings.fields.enforceOnNvr'), type: 'select', defaultValue: String(editRet.enforce), options: [
              { value: 'false', label: t('settings.enforce.monitorOnly') }, { value: 'true', label: t('settings.enforce.pushToNvr') },
            ] },
          ]}
          onClose={() => setEditRet(null)}
          onSubmit={async (v) => {
            await retentionPoliciesApi.update(editRet.id, { minRetentionDays: Math.max(1, Number(v.minDays) || 1), maxRetentionDays: Math.max(1, Number(v.maxDays) || 1), enforce: v.enforce === 'true' });
            queryClient.invalidateQueries({ queryKey: ['retention-policies'] });
            flash(t('settings.toast.retentionSaved', 'Policy updated'));
          }}
        />
      )}
      {editFw && (
        <QuickFormModal
          title={t('settings.firmware.editTitle')}
          submitLabel={t('settings.actions.save')}
          fields={[
            { key: 'vendor', label: t('settings.fields.vendor'), required: true, defaultValue: editFw.vendor },
            { key: 'model', label: t('settings.fields.model'), required: true, defaultValue: editFw.model },
            { key: 'latestVersion', label: t('settings.fields.latestVersion'), required: true, defaultValue: editFw.latestVersion },
            { key: 'notes', label: t('settings.fields.notes'), defaultValue: editFw.notes ?? '' },
          ]}
          onClose={() => setEditFw(null)}
          onSubmit={async (v) => {
            await firmwareCatalogApi.update(editFw.id, { vendor: v.vendor, model: v.model, latestVersion: v.latestVersion, notes: v.notes || null });
            queryClient.invalidateQueries({ queryKey: ['firmware-catalog'] });
            flash(t('settings.toast.firmwareSaved', 'Catalog entry updated'));
          }}
        />
      )}

      {schedOpen && (
        <QuickFormModal
          title={t('settings.schedules.modal.title')}
          submitLabel={t('settings.schedules.modal.submit')}
          fields={[
            { key: 'scope', label: t('settings.fields.scope'), type: 'select', required: true, defaultValue: 'tenant', options: [
              { value: 'tenant', label: t('settings.scope.wholeTenant') }, { value: 'site', label: t('settings.scope.site') }, { value: 'device', label: t('settings.scope.device') },
            ] },
            { key: 'scopeId', label: t('settings.fields.target'), type: 'select', options: scopeOptions },
            { key: 'checkType', label: t('settings.fields.checkType'), type: 'select', defaultValue: 'all', options: [
              { value: 'all', label: t('settings.checkType.all') }, { value: 'heartbeat', label: t('settings.checkType.heartbeat') }, { value: 'recording', label: t('settings.checkType.recording') },
              { value: 'storage', label: t('settings.checkType.storage') }, { value: 'clock', label: t('settings.checkType.clock') }, { value: 'firmware', label: t('settings.checkType.firmware') },
              { value: 'video', label: t('settings.checkType.video') },
            ] },
            { key: 'intervalSec', label: t('settings.fields.intervalSeconds'), type: 'number', required: true, defaultValue: '300', placeholder: '300' },
          ]}
          onClose={() => setSchedOpen(false)}
          onSubmit={async (v) => {
            await monitoringSchedulesApi.create({
              scope: v.scope, scopeId: v.scope === 'tenant' ? undefined : (v.scopeId || undefined),
              checkType: v.checkType || 'all', intervalMs: Math.max(5, Number(v.intervalSec) || 300) * 1000,
            });
            queryClient.invalidateQueries({ queryKey: ['monitoring-schedules'] });
            flash(t('settings.toast.scheduleAdded'));
          }}
        />
      )}

      {retOpen && (
        <QuickFormModal
          title={t('settings.retention.modal.title')}
          submitLabel={t('settings.retention.modal.submit')}
          fields={[
            { key: 'scope', label: t('settings.fields.scope'), type: 'select', required: true, defaultValue: 'tenant', options: [
              { value: 'tenant', label: t('settings.scope.wholeTenant') }, { value: 'site', label: t('settings.scope.site') }, { value: 'device', label: t('settings.scope.device') },
            ] },
            { key: 'scopeId', label: t('settings.fields.target'), type: 'select', options: scopeOptions },
            { key: 'minDays', label: t('settings.fields.minRetentionDays'), type: 'number', required: true, defaultValue: '30', placeholder: '30' },
            { key: 'maxDays', label: t('settings.fields.maxRetentionDays'), type: 'number', required: true, defaultValue: '90', placeholder: '90' },
            { key: 'enforce', label: t('settings.fields.enforceOnNvr'), type: 'select', defaultValue: 'false', options: [
              { value: 'false', label: t('settings.enforce.monitorOnly') }, { value: 'true', label: t('settings.enforce.pushToNvr') },
            ] },
          ]}
          onClose={() => setRetOpen(false)}
          onSubmit={async (v) => {
            await retentionPoliciesApi.create({
              scope: v.scope, scopeId: v.scope === 'tenant' ? undefined : (v.scopeId || undefined),
              minRetentionDays: Math.max(1, Number(v.minDays) || 30),
              maxRetentionDays: Math.max(1, Number(v.maxDays) || 90),
              enforce: v.enforce === 'true',
            });
            queryClient.invalidateQueries({ queryKey: ['retention-policies'] });
            flash(t('settings.toast.retentionAdded'));
          }}
        />
      )}

      {fwOpen && (
        <QuickFormModal
          title={t('settings.firmware.modal.title')}
          submitLabel={t('settings.firmware.modal.submit')}
          fields={[
            { key: 'vendor', label: t('settings.fields.vendor'), required: true, placeholder: 'Dahua' },
            { key: 'model', label: t('settings.fields.model'), required: true, placeholder: 'DHI-NVR4108HS-8P-4KS3' },
            { key: 'latestVersion', label: t('settings.fields.latestVersion'), required: true, placeholder: '4.006.0000000.4.R' },
            { key: 'notes', label: t('settings.fields.notes'), placeholder: t('settings.fields.notesPlaceholder') },
          ]}
          onClose={() => setFwOpen(false)}
          onSubmit={async (v) => {
            await firmwareCatalogApi.create({ vendor: v.vendor, model: v.model, latestVersion: v.latestVersion, notes: v.notes || undefined });
            queryClient.invalidateQueries({ queryKey: ['firmware-catalog'] });
            flash(t('settings.toast.firmwareAdded'));
          }}
        />
      )}

      {addOpen && (
        <QuickFormModal
          title={t('settings.systemTypes.modal.title')}
          submitLabel={t('settings.systemTypes.modal.submit')}
          fields={[
            { key: 'name', label: t('settings.fields.name'), required: true, placeholder: 'CCTV' },
            { key: 'code', label: t('settings.fields.code'), required: true, placeholder: 'cctv' },
            { key: 'category', label: t('settings.fields.category'), placeholder: 'video' },
          ]}
          onClose={() => setAddOpen(false)}
          onSubmit={async (v) => {
            await ticketingApi.createSystemType({ name: v.name, code: v.code, category: v.category || undefined });
            queryClient.invalidateQueries({ queryKey: ['system-types'] });
          }}
        />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
        {/* Settings nav */}
        <div className="tk-card h-fit p-2">
          {TABS.map(({ id, labelKey, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-sm leading-tight ${
                tab === id ? 'bg-white/10 text-fg' : 'text-muted hover:bg-white/5 hover:text-fg'
              }`}
            >
              <Icon size={16} className="shrink-0" />
              <span className="truncate whitespace-nowrap">{t(labelKey)}</span>
            </button>
          ))}
        </div>

        {/* Panel */}
        <div className="space-y-4">
          {tab === 'system-types' && (
            <DataTable columns={stColumns} rows={systemTypes} loading={isLoading} emptyIcon={Cpu} emptyTitle={t('settings.systemTypes.empty')} />
          )}
          {tab === 'fault-types' && (
            <div className="tk-card">
              <div className="mb-3 text-sm font-medium">{t('settings.faultTypes.heading')}</div>
              <div className="space-y-3">
                {(systemTypes ?? []).map((s) => (
                  <div key={s.id} className="flex items-center justify-between border-b border-white/5 pb-3">
                    <span className="text-sm">{s.name}</span>
                    <div className="flex flex-wrap justify-end gap-1">
                      {s.faultTypes.map((f) => <span key={f} className="rounded-full bg-critical/10 px-2 py-0.5 text-[11px] text-critical ring-1 ring-critical/20">{f}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab === 'schedules' && (
            <div className="tk-card">
              <div className="mb-3 text-sm font-medium">{t('settings.schedules.heading')}</div>
              <p className="mb-3 text-xs text-muted">{t('settings.schedules.description')}</p>
              {(schedules ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">{t('settings.schedules.empty')}</p>
              ) : (
                <ul className="text-sm">
                  {(schedules ?? []).map((s) => (
                    <li key={s.id} className="flex items-center justify-between border-b border-white/5 py-2 last:border-0">
                      <span className="flex items-center gap-2">
                        <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-muted ring-1 ring-white/10">{s.scope}</span>
                        <span className="text-muted">{s.checkType}</span>
                        {s.scopeId && <span className="font-medium text-fg">{scopeName(s.scopeId)}</span>}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="tk-num font-medium">{t('settings.schedules.everyInterval', { interval: fmtInterval(s.intervalMs) })}</span>
                        <button className="tk-btn-ghost px-1.5 py-1" title={t('settings.actions.edit')} onClick={() => setEditSched(s)}><Pencil size={14} /></button>
                        <button className="tk-btn-ghost px-1.5 py-1" title={t('settings.actions.delete')} onClick={async () => {
                          await monitoringSchedulesApi.remove(s.id);
                          queryClient.invalidateQueries({ queryKey: ['monitoring-schedules'] });
                          flash(t('settings.toast.scheduleRemoved'));
                        }}><Trash2 size={14} /></button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {tab === 'retention' && (
            <div className="tk-card">
              <div className="mb-3 text-sm font-medium">{t('settings.retention.heading')}</div>
              <p className="mb-3 text-xs text-muted">{t('settings.retention.description')}</p>
              {(retPolicies ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">{t('settings.retention.empty')}</p>
              ) : (
                <ul className="text-sm">
                  {(retPolicies ?? []).map((p) => (
                    <li key={p.id} className="flex items-center justify-between border-b border-white/5 py-2 last:border-0">
                      <span className="flex items-center gap-2">
                        <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-muted ring-1 ring-white/10">{p.scope}</span>
                        {p.scopeId && <span className="font-medium text-fg">{scopeName(p.scopeId)}</span>}
                        {p.enforce && <span className="rounded bg-critical/15 px-1.5 py-0.5 text-[10px] font-medium text-critical">{t('settings.retention.enforceBadge')}</span>}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="tk-num font-medium">{t('settings.retention.daysRange', { min: p.minRetentionDays, max: p.maxRetentionDays })}</span>
                        <button className="tk-btn-ghost px-1.5 py-1" title={t('settings.actions.edit')} onClick={() => setEditRet(p)}><Pencil size={14} /></button>
                        <button className="tk-btn-ghost px-1.5 py-1" title={t('settings.actions.delete')} onClick={async () => {
                          await retentionPoliciesApi.remove(p.id);
                          queryClient.invalidateQueries({ queryKey: ['retention-policies'] });
                          flash(t('settings.toast.retentionRemoved'));
                        }}><Trash2 size={14} /></button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {tab === 'firmware' && (
            <div className="tk-card">
              <div className="mb-3 text-sm font-medium">{t('settings.firmware.heading')}</div>
              <p className="mb-3 text-xs text-muted">{t('settings.firmware.description')}</p>
              {(fwCatalog ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">{t('settings.firmware.empty')}</p>
              ) : (
                <ul className="text-sm">
                  {(fwCatalog ?? []).map((e) => (
                    <li key={e.id} className="flex items-center justify-between border-b border-white/5 py-2 last:border-0">
                      <span className="flex items-center gap-2">
                        <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-muted ring-1 ring-white/10">{e.vendor}</span>
                        <span className="font-medium">{e.model}</span>
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="tk-num font-medium">{e.latestVersion}</span>
                        <button className="tk-btn-ghost px-1.5 py-1" title={t('settings.actions.edit')} onClick={() => setEditFw(e)}><Pencil size={14} /></button>
                        <button className="tk-btn-ghost px-1.5 py-1" title={t('settings.actions.delete')} onClick={async () => {
                          await firmwareCatalogApi.remove(e.id);
                          queryClient.invalidateQueries({ queryKey: ['firmware-catalog'] });
                          flash(t('settings.toast.firmwareRemoved'));
                        }}><Trash2 size={14} /></button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {tab === 'recipients' && (
            <div className="tk-card">
              <div className="mb-1 text-sm font-medium">{t('settings.recipients.heading')}</div>
              <p className="mb-4 text-xs text-muted">{t('settings.recipients.description')}</p>
              {(recipients ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">{t('settings.recipients.empty')}</p>
              ) : (
                <div className="space-y-4">
                  {(recipients ?? []).map((c) => (
                    <RecipientChannelRow
                      key={c.channel}
                      channel={c}
                      channelLabel={t(`settings.channel.${c.channel}`, { defaultValue: c.channel })}
                      onSaved={() => { queryClient.invalidateQueries({ queryKey: ['notification-recipients'] }); flash(t('settings.recipients.saved')); }}
                      onTest={async () => {
                        const r = await notificationRecipientsApi.test(c.channel);
                        if (r.noRecipients) flash(t('settings.recipients.noRecipients'));
                        else flash(t('settings.toast.testSent', { sent: r.sent ?? 0, failed: r.failed ?? 0 }));
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {tab === 'organization' && <DataRetentionCard />}
          {tab === 'assets' && <AssetAutomationCard />}
          {tab === 'security' && <TwoFactorCard />}
        </div>
      </div>
    </div>
  );
}

/**
 * DATA retention (audit CFG-03). This tab used to show four HARDCODED values
 * ("min recording days", "max retention", "90%", "default cadence") that were
 * connected to nothing — while the real TTLs below silently and PERMANENTLY
 * deleted device events, alarms, incidents and audit rows on defaults no one
 * could see or change. This is the real thing, bound to /api/v1/data-retention.
 *
 * NOTE: this is DATABASE-ROW retention. The "Retention" tab is a different
 * thing — NVR *video recording* days.
 */
const RETENTION_FIELDS: RetentionField[] = [
  'eventRetentionDays', 'alarmRetentionDays', 'incidentRetentionDays', 'auditRetentionDays',
];
// The ON/OFF flag paired with each day field (a category is pruned only when on).
const RETENTION_ENABLED_OF: Record<RetentionField, RetentionEnabledField> = {
  eventRetentionDays: 'eventRetentionEnabled',
  alarmRetentionDays: 'alarmRetentionEnabled',
  incidentRetentionDays: 'incidentRetentionEnabled',
  auditRetentionDays: 'auditRetentionEnabled',
};

// One globally-enabled channel: edit this tenant's destination list (one target
// per line) + on/off + save/test. Credentials live in the Control Plane; here we
// only choose WHERE alerts go for this channel.
function RecipientChannelRow({ channel, channelLabel, onSaved, onTest }: {
  channel: NotificationRecipientChannel;
  channelLabel: string;
  onSaved: () => void;
  onTest: () => void | Promise<void>;
}) {
  const { t } = useTranslation('pages');
  const [text, setText] = useState(channel.targets.join('\n'));
  const [enabled, setEnabled] = useState(channel.enabled);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const targets = text.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    try { await notificationRecipientsApi.save(channel.channel, { targets, enabled }); onSaved(); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-white/5 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs capitalize text-primary ring-1 ring-primary/30">{channelLabel}</span>
        </span>
        <Toggle on={enabled} onChange={setEnabled} title={enabled ? t('settings.recipients.channelOnTitle') : t('settings.recipients.channelOffTitle')} />
      </div>
      <textarea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('settings.recipients.targetsPlaceholder')}
        className="tk-input w-full font-mono text-sm"
      />
      <p className="mt-1 text-[11px] text-muted">{t('settings.recipients.targetsHint')}</p>
      <div className="mt-2 flex items-center gap-2">
        <button className="tk-btn-primary px-3 py-1.5 text-sm" disabled={busy} onClick={save}>{t('settings.recipients.save')}</button>
        <button className="tk-btn-ghost px-3 py-1.5 text-sm" onClick={() => onTest()}><Send size={13} /> {t('settings.recipients.test')}</button>
      </div>
    </div>
  );
}

function DataRetentionCard() {
  const { t } = useTranslation('pages');
  const toast = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['data-retention'], queryFn: dataRetentionApi.get, retry: false });
  const [draft, setDraft] = useState<Partial<Record<RetentionField, number>>>({});
  const [enabledDraft, setEnabledDraft] = useState<Partial<Record<RetentionField, boolean>>>({});
  const [saving, setSaving] = useState(false);
  // Pending confirm when the change would delete rows (shorten an enabled TTL, or
  // enable a category with rows already past its cutoff).
  const [confirm, setConfirm] = useState<DataLossImpact['impact'] | null>(null);

  const current = data?.retention;
  const valueOf = (f: RetentionField) => draft[f] ?? current?.[f]?.days ?? 0;
  const enabledOf = (f: RetentionField) => enabledDraft[f] ?? current?.[f]?.enabled ?? false;
  const dirty = RETENTION_FIELDS.some((f) =>
    (draft[f] !== undefined && draft[f] !== current?.[f]?.days) ||
    (enabledDraft[f] !== undefined && enabledDraft[f] !== current?.[f]?.enabled),
  );

  const reset = () => { setDraft({}); setEnabledDraft({}); };

  const save = async (confirmDataLoss = false) => {
    if (!current) return;
    const patch: Partial<Record<RetentionField, number>> & Partial<Record<RetentionEnabledField, boolean>> & { confirmDataLoss?: boolean } = {};
    for (const f of RETENTION_FIELDS) {
      if (draft[f] !== undefined && draft[f] !== current[f].days) patch[f] = draft[f];
      if (enabledDraft[f] !== undefined && enabledDraft[f] !== current[f].enabled) patch[RETENTION_ENABLED_OF[f]] = enabledDraft[f];
    }
    if (Object.keys(patch).length === 0) return;
    if (confirmDataLoss) patch.confirmDataLoss = true;
    setSaving(true);
    try {
      await dataRetentionApi.update(patch);
      await qc.invalidateQueries({ queryKey: ['data-retention'] });
      reset(); setConfirm(null);
      toast.success(t('settings.dataRetention.saved'));
    } catch (e) {
      // 409 = the change would delete rows; the server tells us how many.
      const err = e as { response?: { status?: number; data?: { error?: { code?: string; details?: DataLossImpact } } } };
      if (err?.response?.status === 409 && err.response.data?.error?.code === 'confirm_data_loss_required') {
        setConfirm(err.response.data.error.details?.impact ?? {});
      } else {
        toast.error(t('settings.dataRetention.saveFailed'));
      }
    } finally { setSaving(false); }
  };

  if (isLoading || !current) return <div className="tk-card"><Skeleton className="h-40" /></div>;

  return (
    <div className="tk-card space-y-4">
      <div>
        <div className="text-sm font-medium">{t('settings.dataRetention.heading')}</div>
        <p className="mt-1 text-xs text-muted">{t('settings.dataRetention.warning')}</p>
      </div>

      <div className="space-y-3">
        {RETENTION_FIELDS.map((f) => {
          const on = enabledOf(f);
          return (
            <div key={f} className="flex items-center justify-between gap-4 border-b border-white/5 pb-2 last:border-0">
              <span className="flex min-w-0 items-center gap-3">
                <Toggle on={on} onChange={(v) => setEnabledDraft((d) => ({ ...d, [f]: v }))}
                  title={on ? t('settings.dataRetention.enabledOn', 'Auto-delete on') : t('settings.dataRetention.enabledOff', 'Auto-delete off')} />
                <span className="min-w-0">
                  <span className="block text-sm">{t(`settings.dataRetention.fields.${f}`)}</span>
                  <span className="block truncate text-xs text-muted">{current[f].governs}</span>
                </span>
              </span>
              <span className={`flex shrink-0 items-center gap-2 ${on ? '' : 'pointer-events-none opacity-40'}`}>
                <input
                  type="number" min={7} max={3650} disabled={!on}
                  className="tk-input w-24 py-1 text-sm tk-num"
                  value={valueOf(f)}
                  onChange={(e) => setDraft((d) => ({ ...d, [f]: Math.max(7, Math.min(3650, Number(e.target.value) || 0)) }))}
                />
                <span className="text-xs text-muted">{t('settings.dataRetention.days')}</span>
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-2">
        {dirty && <button className="tk-btn-ghost text-sm" onClick={reset}>{t('settings.dataRetention.cancel')}</button>}
        <button className="tk-btn-primary text-sm" disabled={!dirty || saving} onClick={() => save(false)}>
          {saving ? t('settings.dataRetention.saving') : t('settings.dataRetention.save')}
        </button>
      </div>

      {/* Confirm-before-shorten: shortening a TTL permanently deletes rows. */}
      {confirm && (
        <div className="tk-modal-overlay" onClick={() => setConfirm(null)}>
          <div className="tk-modal max-w-md p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-sm font-medium text-critical">
              <AlertTriangle size={16} /> {t('settings.dataRetention.confirm.title')}
            </div>
            <p className="mt-2 text-sm text-muted">{t('settings.dataRetention.confirm.body')}</p>
            <ul className="mt-3 space-y-1 text-sm">
              {Object.entries(confirm).map(([f, i]) => (
                <li key={f} className="flex items-center justify-between border-b border-white/5 pb-1">
                  <span>{t(`settings.dataRetention.fields.${f}`, f)}</span>
                  <span className="tk-num text-xs">
                    {i.enabling
                      ? <>{t('settings.dataRetention.confirm.enabling', { defaultValue: 'Enabling ({{days}}d)', days: i.to })} · </>
                      : <>{i.from} → {i.to} {t('settings.dataRetention.days')} · </>}
                    <span className="text-critical">{t('settings.dataRetention.confirm.rowsDeleted', { count: i.rowsDeleted })}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <button className="tk-btn-ghost text-sm" onClick={() => setConfirm(null)}>{t('settings.dataRetention.cancel')}</button>
              <button className="tk-btn-primary bg-critical text-sm" disabled={saving} onClick={() => save(true)}>
                {t('settings.dataRetention.confirm.proceed')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Auto-create ASSETS from discovered devices (opt-in per tenant). A toggle +
 * device-category chip multi-select, bound to /api/v1/asset-automation. When on,
 * each driver sync creates/refreshes a lifecycle asset for devices in the chosen
 * categories (deduped by device; operator-edited assets are never overwritten).
 */
function AssetAutomationCard() {
  const { t } = useTranslation('pages');
  const toast = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['asset-automation'], queryFn: assetAutomationApi.get, retry: false });
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [cats, setCats] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);

  // Draft falls back to the server value until edited.
  const on = enabled ?? data?.enabled ?? false;
  const selected = cats ?? data?.categories ?? [];
  const available = data?.availableCategories ?? [];
  const dirty = (enabled !== null && enabled !== data?.enabled)
    || (cats !== null && JSON.stringify([...cats].sort()) !== JSON.stringify([...(data?.categories ?? [])].sort()));

  const toggleCat = (c: string) => {
    const base = cats ?? data?.categories ?? [];
    setCats(base.includes(c) ? base.filter((x) => x !== c) : [...base, c]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await assetAutomationApi.update({ enabled: on, categories: selected });
      await qc.invalidateQueries({ queryKey: ['asset-automation'] });
      setEnabled(null); setCats(null);
      toast.success(t('settings.assetAutomation.saved'));
    } catch {
      toast.error(t('settings.assetAutomation.saveFailed'));
    } finally { setSaving(false); }
  };

  if (isLoading || !data) return <div className="tk-card"><Skeleton className="h-40" /></div>;

  return (
    <div className="tk-card space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-medium">{t('settings.assetAutomation.heading')}</div>
          <p className="mt-1 text-xs text-muted">{t('settings.assetAutomation.description')}</p>
        </div>
        <Toggle on={on} onChange={setEnabled} title={on ? t('settings.assetAutomation.onTitle') : t('settings.assetAutomation.offTitle')} />
      </div>

      <div className={on ? '' : 'pointer-events-none opacity-40'}>
        <div className="mb-2 text-xs font-medium text-muted">{t('settings.assetAutomation.categoriesLabel')}</div>
        <div className="flex flex-wrap gap-1.5">
          {available.map((c) => {
            const isOn = selected.includes(c);
            return (
              <button key={c} type="button" onClick={() => toggleCat(c)}
                className={`rounded-full px-2.5 py-1 text-xs ring-1 transition-colors ${isOn ? 'bg-primary/15 text-primary ring-primary/40' : 'bg-white/5 text-muted ring-white/10 hover:bg-white/10'}`}>
                {isOn && <Check size={11} className="mr-0.5 inline" />}{t(`settings.assetAutomation.categories.${c}`, c)}
              </button>
            );
          })}
        </div>
        {selected.length === 0 && <p className="mt-2 text-[11px] text-warning">{t('settings.assetAutomation.noneSelected')}</p>}
      </div>

      <div className="flex justify-end">
        <button className="tk-btn-primary text-sm" disabled={!dirty || saving} onClick={save}>{t('settings.assetAutomation.save')}</button>
      </div>
    </div>
  );
}
